<template>
  <p-product-layout
    data-test="product-layout"
    :product="product"
    :is-authenticated="isAuthenticated"
    :cooperative-name="cooperativeName"
    :technical-sales="profile.technicalSales"
    :show-technical-sales-contact="showTechnicalSalesContact"
    :banner="banner"
    @login="login()"
    @go-to-product-search="goToProductSearch"
  >
    <template #product-alert>
      <p-alert
        v-if="featuresForCurrentBrand.certificates.phyto && cooperativeName && !isCropProtectionAllowed"
        level="error"
        data-test="crop-protection-alert-error"
      >
        {{
          i18n._(
            'Aladin ne dispose pas de votre certiphyto, merci de contacter votre coopérative\
 afin de pouvoir commander les produits santé du végétal sur la plateforme',
          )
        }}
      </p-alert>
    </template>

    <template v-if="isPhyto" #header-market-authorization>
      <div
        v-for="marketAuthorization in marketAuthorizations"
        :key="marketAuthorization"
        data-test="market-authorizations"
      >
        <div data-test="market-authorization" class="product-crop-protection__market-authorization">
          {{ marketAuthorization }}
        </div>
      </div>
    </template>
    <template #header-regulatory-display>
      <div>
        <p-product-alert-regulatory id="product-regulatory" :product="product" data-test="regulatory" />
      </div>
    </template>
    <template #default>
      <p-product-detail-block id="product-detail">
        <div class="pack-product-detail-composition">
          <div v-if="hasActiveSubstances" data-test="composition" class="pack-product-detail-composition__column">
            <h2 class="pack-product-detail-composition__column-title" data-test="composition-title">
              {{ i18n._('Matières actives') }}
            </h2>
            <div
              v-for="activeSubstance in product.active_substances"
              :key="activeSubstance.product_name"
              class="product-active-substances__item-wrapper"
              data-test="per-product-active-substances"
            >
              <div>
                <h2 data-test="active-substance__product-name" class="active-substance__product-name">
                  {{ activeSubstance.product_name }}
                </h2>
                <div
                  v-for="substance in activeSubstance.value"
                  :key="substance.name"
                  class="product-active-substances__product-item"
                >
                  <div
                    class="product-active-substances__item"
                    data-test="product-active-substances__item"
                    @click="searchActiveSubstance(substance.name)"
                  >
                    <span class="product-active-substances__quantity">{{ substance.value }} {{ substance.unit }}</span>
                    <span class="product-active-substances__separation">-</span>
                    <span class="product-active-substances__name">{{ substance.name }}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div
            v-if="hasFormulation"
            data-test="formulation"
            :title="i18n._('Formulation')"
            class="pack-product-detail-composition__column"
          >
            <h2 class="pack-product-detail-composition__column-title" data-test="formulation-title">
              {{ i18n._('Formulation') }}
            </h2>
            <div
              v-for="(formulation, index) in product.formulation"
              :key="'formulation-' + index"
              data-test="formulation-item"
            >
              {{ formulation.product_name + ' - ' + formulation.value }}
            </div>
          </div>
        </div>
      </p-product-detail-block>
      <p-product-detail-block-html
        class="product-crop-protection__description"
        :html="product.description"
        :title="i18n._('Descriptif')"
        data-test="description"
        :format-html="true"
      />
      <p-product-detail-block
        v-if="cooperativeName && isPhyto"
        class="product-crop-protection__adivalor"
        data-test="adivalor-notice"
      >
        <p>
          <img
            src="@b2ag/themes/aladin/assets/logo_adivalor.svg"
            class="product-crop-protection__adivalor-icon"
            alt="Advilor logo"
          />
          {{ cooperativeName }} {{ i18n._('gère le traitement des EVPP et des PPNU en coopération avec ADIVALOR') }}
        </p>
      </p-product-detail-block>
      <p-product-detail-block-html
        :html="product.characteristics"
        :title="i18n._('Caractéristiques')"
        data-test="characteristics"
      />
      <p-product-detail-block
        class="product-biocide-use"
        v-if="product.biocide_use && product.biocide_use.length > 0"
        :title="i18n._('Usage biocide')"
      >
        <p v-for="biocide in product.biocide_use" :key="biocide.code">
          {{ biocide.label }}
        </p>
      </p-product-detail-block>
      <p-product-detail-block-review
        id="product-review"
        data-test="review"
        :cooperative-review="product.cooperativeReview"
        :is-pack="true"
        :cooperative-name="cooperativeName"
      />
      <p-pack-product-usages id="product-usages" :usages_by_crops="product.usages_by_crops" data-test="usages" />

      <p-product-detail-block
        v-if="product.toxicities"
        data-test="toxicity"
        :title="i18n._('Classification toxicologique')"
      >
        <p-product-detail-block-toxicity
          v-for="toxicity in product.toxicities"
          :key="'toxicity-' + toxicity.product_name"
          class="pack-product-crop-protection__toxicities"
          :toxicities="toxicity.value"
          :product-name="toxicity.product_name"
        />
      </p-product-detail-block>

      <p-product-detail-block
        v-if="product.toxicities"
        data-test="security-recommendations"
        :title="i18n._('Recommandations de sécurité')"
      >
        <p-product-detail-block-security-recommendations
          v-for="toxicity in product.toxicities"
          :key="'security-recommendations-' + toxicity.product_name"
          class="pack-product-crop-protection__security-recommendations"
          :toxicities="toxicity.value"
          :product-name="toxicity.product_name"
        />
      </p-product-detail-block>
      <p-product-detail-block-documentation
        id="product-documentation"
        :product="product"
        :suffix="isPhyto ? i18n._('(dont étiquette)') : ''"
        data-test="documentation"
      />
    </template>

    <template v-if="isPhyto" #legal-mention>
      <div data-test="legal-mention" class="product-crop-protection__phyto-legal-mention">
        <div class="product-crop-protection__phyto-legal-mention-main-message">
          {{
            i18n._(
              `Produits pour les professionnels : utilisez les produits phytopharmaceutiques avec précaution. Avant toute utilisation, lisez l'étiquette et les informations concernant le produit.`,
            )
          }}
        </div>
        <div>
          {{
            i18n._(`Avant toute utilisation assurez-vous que celle-ci est indispensable. Privilégiez chaque fois que possible les
          méthodes alternatives et les produits présentant le risque le plus faible pour la santé humaine et animale et
          pour l'environnement, conformément aux principes de la protection intégrée, consultez`)
          }}
          <a href="http://www.agriculture.gouv.fr/ecophyto" target="_blank">
            {{ i18n._('http://www.agriculture.gouv.fr/ecophyto') }}
          </a>
          .
        </div>
      </div>
    </template>
  </p-product-layout>
</template>

<script lang="ts">
import type { PropType } from 'vue'
import { computed, defineComponent } from 'vue'
import PPackProductUsages from '@b2ag/usages/src/components/PPackProductUsages/PPackProductUsages.vue'
import { featuresForCurrentBrand } from '@b2ag/app-brands/dist/features'
import PAlert from '@b2ag/design-system/src/presenters/PAlert/PAlert.vue'
import { i18n } from '@b2ag/locale'
import PProductDetailBlock from '../PProductDetailBlock/PProductDetailBlock.vue'
import PProductDetailBlockHtml from '../PProductDetailBlockHtml/PProductDetailBlockHtml.vue'
import PProductDetailBlockDocumentation from '../PProductDetailBlockDocumentation/PProductDetailBlockDocumentation.vue'
import PProductAlertRegulatory from '../PProductAlertRegulatory/PProductAlertRegulatory.vue'
import PProductDetailBlockReview from '../PProductDetailBlockReview/PProductDetailBlockReview.vue'
import PProductDetailBlockSecurityRecommendations from '../PProductDetailBlockSecurityRecommendations/PProductDetailBlockSecurityRecommendations.vue'
import PProductDetailBlockToxicity from '../PProductDetailBlockToxicity/PProductDetailBlockToxicity.vue'
import PProductLayout from '../PProductLayout/PProductLayout.vue'
import { DisplayType, type Pack } from '../typings'
import type { Banner } from '@b2ag/contexts/marketing/src/domain/blocs/Banner'
import { CROP_PROTECTION_KIND } from '../../domain/products.constants'

export default defineComponent({
  name: 'PPackProductB2AG',
  components: {
    PProductLayout,
    PProductDetailBlock,
    PProductDetailBlockHtml,
    PProductDetailBlockDocumentation,
    PProductAlertRegulatory,
    PProductDetailBlockReview,
    PProductDetailBlockToxicity,
    PProductDetailBlockSecurityRecommendations,
    PPackProductUsages,
    PAlert,
  },
  props: {
    product: {
      type: Object as PropType<Pack>,
      required: true,
      default: () => ({}),
    },
    profile: {
      type: Object,
      default: () => ({}),
    },
    isAuthenticated: {
      type: Boolean,
      default: false,
    },
    showTechnicalSalesContact: {
      type: Boolean,
      default: true,
    },
    cooperativeName: {
      type: String,
      default: '',
    },
    isCropProtectionAllowed: {
      type: Boolean,
      default: false,
    },
    banner: {
      type: Object as PropType<Banner>,
      default: () => {},
    },
  },
  setup(props, { emit }) {
    const marketAuthorizations = computed(() => {
      return (
        props.product.market_authorization?.map((authorization) => {
          return `AMM ${authorization.product_name} : ${authorization.value}`
        }) ?? ['AMM : NC']
      )
    })
    const hasActiveSubstances = computed(() => {
      return (props.product.active_substances ?? []).length > 0
    })
    const hasFormulation = computed(() => {
      return (props.product.formulation ?? []).length > 0
    })

    const isPhyto = computed(
      () => props.product.displayType === DisplayType.PHYTO || props.product.kind === CROP_PROTECTION_KIND,
    )

    const searchActiveSubstance = (activeSubstance: string): void => emit('searchActiveSubstance', activeSubstance)
    const login = () => emit('login')
    const goToProductSearch = (goToProductSearchArgs: unknown) => emit('goToProductSearch', goToProductSearchArgs)

    return {
      marketAuthorizations,
      hasActiveSubstances,
      hasFormulation,
      searchActiveSubstance,
      login,
      goToProductSearch,
      featuresForCurrentBrand,
      i18n,
      isPhyto,
    }
  },
})
</script>

<style scoped lang="scss">
.product-active-substances {
  @media (min-width: $breakpoint-md) {
    margin: 0 0 2em;
    overflow: hidden;
    flex: 50%;
    bottom: 0;
    padding: 0;
    border: 0;
  }

  &__item-wrapper:not(:last-child) {
    margin-bottom: 20px;
  }

  &__product-item {
    margin-bottom: 5px;
  }

  &__item {
    border: 1px solid black;
    border-radius: 32px;
    font-weight: normal;
    font-size: 1.6rem;
    line-height: 22px;
    padding: 5px 10px;
    display: inline-block;
    cursor: pointer;
  }

  &__separation {
    padding: 0 5px;
  }
}

.product-crop-protection__market-authorization {
  display: block;
  font-weight: normal;
  text-transform: uppercase;
  font-size: var(--font-size-s);
  height: 1.2em;

  @media (min-width: $breakpoint-sm) {
    font-size: var(--font-size-m);
    margin: 5px 0;
  }
}

.active-substance__product-name {
  font-size: 1em;
}

.pack-product-detail-composition {
  display: flex;
  flex-direction: column;

  &__column {
    flex: 1;
    margin-bottom: 30px;

    @media (min-width: $breakpoint-lg) {
      flex-direction: row;
      margin: 0;
    }

    &-title {
      text-transform: uppercase;
      font-size: 1em;
      font-weight: bold;
      line-height: 22px;
      margin-bottom: 1em;
    }
  }

  @media (min-width: $breakpoint-lg) {
    flex-direction: row;
  }
}

.pack-product-crop-protection__security-recommendations {
  border: none;
  margin-top: 0;
  padding: 0 0 30px;

  &:last-child {
    padding: 0;
  }
}

.pack-product-crop-protection__toxicities {
  border: none;
  margin-top: 0;
  padding: 0 0 35px;

  &:last-child {
    padding: 0 0 10px;
  }
}

.product-crop-protection__phyto-legal-mention {
  color: var(--color-inactive-dark);
  margin: 15px;
  font-size: 0.9em;

  @media (min-width: $breakpoint-lg) {
    margin-bottom: 50px;
  }
}

.product-crop-protection__phyto-legal-mention-main-message {
  font-weight: bold;
  margin-bottom: 5px;
}

.product-crop-protection__adivalor {
  color: var(--color-text-light);
}

.product-crop-protection__adivalor-icon {
  width: 24px;
  height: 24px;
  margin-right: 5px;
}

.product-crop-protection__description + .product-crop-protection__adivalor {
  margin-top: 0;
  border-top: none;
}

.product-biocide-use {
  margin-top: 0;
  border-top: none;
  p {
    font-size: var(--font-size-m);
    color: var(--color-text-light);
  }
}
</style>
